import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  ActionFunctions,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {DEFAULT_COLUMN_WIDTH} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {Table, TableReorderLocks} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';

export interface AppCanvasStructuredTableMetadata {
  columns: Array<DataFrameColumnDef>;
  columnWidths: {[key: number]: number};
}

export interface AppCanvasStructuredTableRowMetadata {}

export type AppCanvasStructuredTable = StructuredTable<
  AppCanvasStructuredTableMetadata,
  AppCanvasStructuredTableRowMetadata
>;

function convertColumns(
  columns: Array<DataFrameColumnDef>,
  columnWidths: {[key: string]: number}
): Array<StructuredTableColumn> {
  const STColumns: Array<StructuredTableColumn> = [];
  for (const [idx, col] of columns.entries()) {
    const width = columnWidths[idx] ?? DEFAULT_COLUMN_WIDTH;
    if (Object.values<string>(SystemColumnName).includes(col.systemName)) {
      continue;
    }
    const STCol = makeStructuredTableColumn({
      name: col.displayName,
      columnType: TableColumnType.STRING,
      width,
    });
    STColumns.push(STCol);
  }
  return STColumns;
}

export default class AppCanvasStructuredTableAdapter extends GenericStructuredTableAdapter<
  AppCanvasStructuredTableMetadata,
  AppCanvasStructuredTableRowMetadata
> {
  static getAdapter(): AppCanvasStructuredTableAdapter {
    return getStructuredTableAdapters().APP_CANVAS as AppCanvasStructuredTableAdapter;
  }

  override getActionFunctions(
    tableMetadata: AppCanvasStructuredTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<AppCanvasStructuredTableMetadata, AppCanvasStructuredTableRowMetadata> {
    return {
      secondary: [],
      overflow: [downloadCSVActionFunction],
    };
  }

  getBlankRowMetadata(tableMetadata: AppCanvasStructuredTableMetadata): AppCanvasStructuredTableRowMetadata {
    return {};
  }

  getDisplayName(structuredTable: AppCanvasStructuredTable): string {
    return 'Canvas Table';
  }

  override _getAllSupportedColumns({
    metadata,
  }: {
    metadata: AppCanvasStructuredTableMetadata;
  }): Array<StructuredTableColumn> {
    return convertColumns(metadata.columns, metadata.columnWidths);
  }

  override toTable(canvasTable: AppCanvasStructuredTable): Table {
    const table = super.toTable(canvasTable);
    return {...table, allowColumnEdits: false, lockedDims: true, allowNoRows: true};
  }

  override allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: false,
      [TableDimension.COLUMN]: false,
    };
  }
}
